import React, { useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  MoreVert,
  Delete,
  Send,
  Close,
  Preview,
  BarChart,
  Download,
  Publish,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../Layouts/Layout";
import { BlockingLoader } from "../Components/BlockingLoader";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { extractErrorMessage } from "../utils/errorUtils";
import { CampaignProgress } from "../Components/Campaign/Progress";
import { SubmissionReviewState } from "../Components/SubmissionReview/State";
import { GridList, MappedItem } from "../Components/MapList";
import {
  GET_SUBMISSION_REVIEW_ACTIVITY,
  UPDATE_SUBMISSION_REVIEW_ACTIVITY,
  DELETE_SUBMISSION_REVIEW_ACTIVITY,
  OPEN_SUBMISSIONS,
  CLOSE_SUBMISSIONS,
  REOPEN_SUBMISSIONS,
  ALLOCATE_REVIEWS,
  OPEN_REVIEWS,
  CLOSE_REVIEWS,
  RELEASE_RESULTS,
  SEND_SUBMISSION_REMINDERS,
  SEND_REVIEW_REMINDERS,
  PUSH_SUBMISSION_REVIEW_GRADES,
  GET_FORMS,
  FormsResult,
  GET_GROUP_CATEGORIES,
  GroupCategoriesResult,
} from "../Queries";
import {
  SubmissionReviewActivity,
  SubmissionReviewStatus,
  GroupCategory,
} from "../types";
import { ActivityDetails } from "../Components/SubmissionReview/ActivityDetails";
import { ParticipantsList } from "../Components/SubmissionReview/ParticipantsList";
import { ActivityLogView } from "../Components/SubmissionReview/ActivityLogView";

export function EditSubmissionReview() {
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Queries
  const { data, loading, refetch } = useQuery(GET_SUBMISSION_REVIEW_ACTIVITY, {
    variables: { pk: activityId },
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error loading activity"))),
        error,
      ),
  });

  const { data: formsData } = useQuery<FormsResult>(GET_FORMS, {
    variables: { filters: {} },
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error loading templates"))),
        error,
      ),
  });

  const { data: categoriesData } = useQuery<GroupCategoriesResult>(
    GET_GROUP_CATEGORIES,
    {
      variables: { filters: {} },
      onError: (error) =>
        setError(
          extractErrorMessage(
            error,
            String(t("Error loading group categories")),
          ),
          error,
        ),
    },
  );

  // Mutations
  const [updateActivity] = useMutation(UPDATE_SUBMISSION_REVIEW_ACTIVITY, {
    onError: (error) =>
      setError(extractErrorMessage(error, String(t("Error updating"))), error),
  });
  const [deleteActivity] = useMutation(DELETE_SUBMISSION_REVIEW_ACTIVITY);
  const [openSubmissions] = useMutation(OPEN_SUBMISSIONS);
  const [closeSubmissions] = useMutation(CLOSE_SUBMISSIONS);
  const [reopenSubmissions] = useMutation(REOPEN_SUBMISSIONS);
  const [allocateReviewsMut] = useMutation(ALLOCATE_REVIEWS);
  const [openReviews] = useMutation(OPEN_REVIEWS);
  const [closeReviews] = useMutation(CLOSE_REVIEWS);
  const [releaseResults] = useMutation(RELEASE_RESULTS);
  const [sendSubmissionReminders] = useMutation(SEND_SUBMISSION_REMINDERS);
  const [sendReviewReminders] = useMutation(SEND_REVIEW_REMINDERS);
  const [pushGrades] = useMutation(PUSH_SUBMISSION_REVIEW_GRADES);

  const activity: SubmissionReviewActivity | undefined =
    data?.submissionReviewActivity;
  const templates = formsData?.forms ?? [];
  const categories = categoriesData?.groupCategories ?? [];
  const showLoading = loading && !activity;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleChange = useCallback(
    (updated: SubmissionReviewActivity, label: string) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        updateActivity({
          variables: {
            id: updated.id,
            input: {
              name: updated.name,
              description: updated.description,
              submissionType: updated.submissionType,
              reviewsPerSubmission: updated.reviewsPerSubmission,
              submitterMode: updated.submitterMode,
              reviewerMode: updated.reviewerMode,
              allowSelfReview: updated.allowSelfReview,
              anonymityMode: updated.anonymityMode,
              maxPoints: updated.maxPoints,
              submissionWeight: updated.submissionWeight,
              reviewWeight: updated.reviewWeight,
              participationWeight: updated.participationWeight,
              loopStudent: updated.loopStudent,
              loopInstructor: updated.loopInstructor,
              minWordCount: updated.minWordCount,
              rubricId: updated.rubricId,
              groupcategoryId: updated.groupcategoryId,
              openDate: updated.openDate,
              dueDate: updated.dueDate,
              closeDate: updated.closeDate,
              reviewOpenDate: updated.reviewOpenDate,
              reviewDueDate: updated.reviewDueDate,
              reviewCloseDate: updated.reviewCloseDate,
              resultsReleaseDate: updated.resultsReleaseDate,
            },
          },
          context: { debounceKey: `update-activity-${updated.id}` },
        }).then(() => refetch());
      }, 500);
    },
    [updateActivity, refetch],
  );

  const handleSetGroupCategory = async (
    act: SubmissionReviewActivity,
    gc: GroupCategory | undefined,
  ) => {
    await updateActivity({
      variables: {
        id: act.id,
        input: {
          name: act.name,
          description: act.description,
          submissionType: act.submissionType,
          reviewsPerSubmission: act.reviewsPerSubmission,
          submitterMode: act.submitterMode,
          reviewerMode: act.reviewerMode,
          allowSelfReview: act.allowSelfReview,
          anonymityMode: act.anonymityMode,
          maxPoints: act.maxPoints,
          submissionWeight: act.submissionWeight,
          reviewWeight: act.reviewWeight,
          participationWeight: act.participationWeight,
          loopStudent: act.loopStudent,
          loopInstructor: act.loopInstructor,
          minWordCount: act.minWordCount,
          rubricId: act.rubricId,
          groupcategoryId: gc?.id ?? null,
          openDate: act.openDate,
          dueDate: act.dueDate,
          closeDate: act.closeDate,
          reviewOpenDate: act.reviewOpenDate,
          reviewDueDate: act.reviewDueDate,
          reviewCloseDate: act.reviewCloseDate,
          resultsReleaseDate: act.resultsReleaseDate,
        },
      },
    });
    refetch();
  };

  const setActivityName = (name: string) => {
    if (!activity || name === activity.name) return;
    handleChange({ ...activity, name }, "Rename activity");
  };

  const handleTransition = async (
    mutation: typeof openSubmissions,
    message: string,
  ) => {
    if (!activity) return;
    setIsTransitioning(true);
    setTransitionMessage(message);
    try {
      await mutation({ variables: { activityId: activity.id } });
      await refetch();
    } catch (error) {
      setError(
        extractErrorMessage(error as any, String(t("Transition failed"))),
        error as Error,
      );
    } finally {
      setIsTransitioning(false);
      setTransitionMessage("");
    }
  };

  const handleAllocateAndOpenReviews = async () => {
    if (!activity) return;
    setIsTransitioning(true);
    setTransitionMessage("Allocating reviews and opening review phase...");
    try {
      await allocateReviewsMut({ variables: { activityId: activity.id } });
      await openReviews({ variables: { activityId: activity.id } });
      await refetch();
    } catch (error) {
      setError(
        extractErrorMessage(error as any, String(t("Error"))),
        error as Error,
      );
    } finally {
      setIsTransitioning(false);
      setTransitionMessage("");
    }
  };

  const handlePushGrades = async () => {
    if (!activity) return;
    setIsTransitioning(true);
    setTransitionMessage("Pushing grades to the LMS...");
    try {
      const result = await pushGrades({
        variables: { activityId: activity.id },
      });
      const payload = result.data?.pushSubmissionReviewGrades;
      if (!payload?.ok) {
        throw new Error(payload?.message || "Grade push failed");
      }
      await refetch();
    } catch (error) {
      setError(
        extractErrorMessage(error as any, String(t("Error pushing grades"))),
        error as Error,
      );
    } finally {
      setIsTransitioning(false);
      setTransitionMessage("");
    }
  };

  const handleDelete = async () => {
    if (!activity) return;
    await deleteActivity({ variables: { id: activity.id } });
    navigate("/submissions");
  };

  const handleRemind = async () => {
    if (!activity) return;
    setIsTransitioning(true);
    setTransitionMessage("Sending reminders...");
    try {
      if (activity.status === SubmissionReviewStatus.SubmissionsOpen) {
        await sendSubmissionReminders({
          variables: { activityId: activity.id },
        });
      } else if (activity.status === SubmissionReviewStatus.ReviewsOpen) {
        await sendReviewReminders({
          variables: { activityId: activity.id },
        });
      }
    } catch (error) {
      setError(
        extractErrorMessage(error as any, String(t("Error sending reminders"))),
        error as Error,
      );
    } finally {
      setIsTransitioning(false);
      setTransitionMessage("");
    }
  };

  if (!activity && !showLoading) return null;

  const isDraft = activity?.status === SubmissionReviewStatus.Draft;
  const isLive = activity?.submissionType === "LIV";
  const canRemind =
    activity?.status === SubmissionReviewStatus.SubmissionsOpen ||
    activity?.status === SubmissionReviewStatus.ReviewsOpen;

  // Find selected rubric name
  const selectedRubric = templates.find((f) => f.id === activity?.rubricId);

  // ── State metadata for display ───────────────────────────────────────────

  const anonymityLabels: Record<string, string> = {
    DB: "Double-blind",
    SB: "Single-blind",
    ID: "Identified",
  };

  const gradesSyncedRow: MappedItem[] = activity?.gradesSyncedToLms
    ? [
        {
          label: String(t("Grades synced")),
          value: (
            <Typography variant="body2">
              {new Date(activity.gradesSyncedToLms).toLocaleString()}
            </Typography>
          ),
        },
      ]
    : [];

  const stateMetadata: MappedItem[] = activity
    ? [
        {
          label: String(t("Rubric")),
          value: (
            <Typography variant="body2">
              {selectedRubric?.name || String(t("Not selected"))}
            </Typography>
          ),
        },
        {
          label: String(t("Anonymity")),
          value: (
            <Typography variant="body2">
              {anonymityLabels[activity.anonymityMode] || "Double-blind"}
            </Typography>
          ),
        },
        ...gradesSyncedRow,
      ]
    : [];

  // ── Primary action button ────────────────────────────────────────────────

  const renderPrimaryAction = () => {
    if (!activity) return null;
    const noRubric = !activity.rubricId;
    switch (activity.status) {
      case SubmissionReviewStatus.Draft:
        // Live presentations have no submission phase — the presentation
        // happens in class, so Draft goes straight to Reviews Open.
        if (isLive) {
          return (
            <Tooltip
              title={
                noRubric
                  ? String(t("Please select a rubric before opening reviews"))
                  : ""
              }
            >
              <span>
                <LoadingButton
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() =>
                    handleTransition(openReviews, "Opening reviews...")
                  }
                  disabled={noRubric}
                  size="small"
                >
                  {t("Open Reviews")}
                </LoadingButton>
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip
            title={
              noRubric
                ? String(t("Please select a rubric before opening submissions"))
                : ""
            }
          >
            <span>
              <LoadingButton
                variant="contained"
                startIcon={<Send />}
                onClick={() =>
                  handleTransition(openSubmissions, "Opening submissions...")
                }
                disabled={noRubric}
                size="small"
              >
                {t("Open Submissions")}
              </LoadingButton>
            </span>
          </Tooltip>
        );
      case SubmissionReviewStatus.SubmissionsOpen:
        return (
          <LoadingButton
            variant="contained"
            startIcon={<Close />}
            onClick={() =>
              handleTransition(closeSubmissions, "Closing submissions...")
            }
            size="small"
          >
            {t("Close Submissions")}
          </LoadingButton>
        );
      case SubmissionReviewStatus.SubmissionsClosed:
        return (
          <>
            {/* FL-284: SC -> SO reopen — safe until reviews are allocated */}
            <LoadingButton
              variant="outlined"
              onClick={() =>
                handleTransition(reopenSubmissions, "Reopening submissions...")
              }
              size="small"
              sx={{ mr: 1 }}
            >
              {t("Reopen Submissions")}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              startIcon={<Send />}
              onClick={handleAllocateAndOpenReviews}
              size="small"
            >
              {t("Allocate & Open Reviews")}
            </LoadingButton>
          </>
        );
      case SubmissionReviewStatus.ReviewsOpen:
        return (
          <LoadingButton
            variant="contained"
            startIcon={<Close />}
            onClick={() => handleTransition(closeReviews, "Closing reviews...")}
            size="small"
          >
            {t("Close Reviews")}
          </LoadingButton>
        );
      case SubmissionReviewStatus.ReviewsClosed:
        return (
          <LoadingButton
            variant="contained"
            startIcon={<Preview />}
            onClick={() =>
              handleTransition(releaseResults, "Releasing results...")
            }
            size="small"
          >
            {t("Release Results")}
          </LoadingButton>
        );
      default:
        return null;
    }
  };

  return (
    <Layout
      breadcrumbs={[
        {
          title: String(t("navigation.submissions")),
          link: "/submissions",
        },
        { title: activity?.name ?? "..." },
      ]}
      loading={showLoading}
    >
      <BlockingLoader open={isTransitioning} message={transitionMessage} />

      {activity && (
        <>
          {/* ── State Management Card (matches Campaign/Edit pattern) ─── */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              action={
                <>
                  <SubmissionReviewState
                    sx={{ ml: 2 }}
                    status={activity.status}
                  />
                  <span>&nbsp;</span>
                  {activity.status !== SubmissionReviewStatus.Draft && (
                    <Tooltip title={String(t("View Results"))}>
                      <IconButton
                        sx={{ m: 0.5 }}
                        color="success"
                        onClick={() =>
                          navigate(`/submissions/${activity.id}/results`)
                        }
                      >
                        <BarChart />
                      </IconButton>
                    </Tooltip>
                  )}
                  {renderPrimaryAction()}
                  <span>&nbsp;</span>
                  <IconButton
                    aria-label="more"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={() => setAnchorEl(null)}
                    onClick={() => setAnchorEl(null)}
                  >
                    {isDraft && (
                      <MenuItem onClick={handleDelete}>
                        <ListItemIcon>
                          <Delete fontSize="small" />
                        </ListItemIcon>
                        {t("Delete")}
                      </MenuItem>
                    )}
                    {canRemind && (
                      <MenuItem onClick={handleRemind}>
                        <ListItemIcon>
                          <Send fontSize="small" />
                        </ListItemIcon>
                        {t("Send Reminder")}
                      </MenuItem>
                    )}
                    {activity.status !== SubmissionReviewStatus.Draft && (
                      <MenuItem
                        component="a"
                        href={`/api/submission-reviews/${activity.id}/export.csv`}
                        target="_blank"
                      >
                        <ListItemIcon>
                          <Download fontSize="small" />
                        </ListItemIcon>
                        {t("Export CSV")}
                      </MenuItem>
                    )}
                    {activity &&
                      activity.status !== SubmissionReviewStatus.Draft && (
                        <MenuItem
                          component="a"
                          href={`/api/submission-reviews/${activity.id}/export.pdf`}
                          target="_blank"
                        >
                          <ListItemIcon>
                            <Download fontSize="small" />
                          </ListItemIcon>
                          {t("Export PDF")}
                        </MenuItem>
                      )}
                    {activity.status ===
                      SubmissionReviewStatus.ResultsReleased && (
                      <MenuItem onClick={handlePushGrades}>
                        <ListItemIcon>
                          <Publish fontSize="small" />
                        </ListItemIcon>
                        {activity.gradesSyncedToLms
                          ? t("Push Grades to LMS Again")
                          : t("Push Grades to LMS")}
                      </MenuItem>
                    )}
                  </Menu>
                </>
              }
              title={
                <TextField
                  variant="outlined"
                  label={String(t("Activity name"))}
                  inputProps={{ maxLength: 255 }}
                  defaultValue={activity.name}
                  fullWidth
                  size="small"
                  onBlur={(e) => setActivityName(e.target.value.trim())}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    setActivityName(
                      (e.target as unknown as { value: string }).value.trim(),
                    );
                  }}
                />
              }
            />
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <GridList items={stateMetadata} />
                {activity.status !== SubmissionReviewStatus.Draft && (
                  <Box sx={{ display: "flex", gap: 3 }}>
                    {/* No submission phase for live presentations, so no
                        submission progress to wait on */}
                    {!isLive && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("Submissions")}
                        </Typography>
                        <CampaignProgress
                          submitted={activity.submittedCount ?? 0}
                          total={activity.submissionCount ?? 0}
                        />
                      </Box>
                    )}
                    {(activity.totalReviews ?? 0) > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("Reviews")}
                        </Typography>
                        <CampaignProgress
                          submitted={activity.completedReviews ?? 0}
                          total={activity.totalReviews ?? 0}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* ── Activity Configuration ──────────────────────────────────── */}
          <ActivityDetails
            activity={activity}
            templates={templates}
            categories={categories}
            onChange={handleChange}
            onSetGroupCategory={handleSetGroupCategory}
            readOnly={!isDraft}
          />

          {/* ── Participants (visible after submissions open) ───────────── */}
          {activity.submissions && activity.submissions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <ParticipantsList
                activity={activity}
                submissions={activity.submissions}
                onRefresh={refetch}
              />
            </Box>
          )}

          {/* ── Activity Log ────────────────────────────────────────────── */}
          {activity.activityLogs && activity.activityLogs.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <ActivityLogView entries={activity.activityLogs} />
            </Box>
          )}
        </>
      )}
    </Layout>
  );
}
